import { DatabaseOrTempDtoId } from "client/Api/common";
import { Guid } from "client_legacy/ndaw/guid";
import { NormalizedMap } from "./normalizedMap";

interface IDatabaseId {
	readonly databaseId: number;
	readonly hasDatabaseId: true;
}

interface ITempId {
	readonly tempId: Guid;
	readonly hasDatabaseId: false;
}

interface IDatabaseOrTempObjectIdEquatable {
	equals(other: DatabaseOrTempObjectId): boolean;
}

interface IDatabaseOrTempObjectIdSerializable {
	toString(): string;
}

class DatabaseId implements
	IDatabaseId,
	IDatabaseOrTempObjectIdEquatable,
	IDatabaseOrTempObjectIdSerializable
{
	public readonly databaseId: number;
	public readonly hasDatabaseId: true;

	constructor(databaseId: number) {
		this.databaseId = databaseId;
		this.hasDatabaseId = true;
	}

	public equals(other: DatabaseOrTempObjectId) {
		if (!other.hasDatabaseId) {
			return false;
		}

		return other.databaseId === this.databaseId;
	}

	public toString(): string {
		return `db_${this.databaseId}`;
	}
}

class TempId implements
	ITempId,
	IDatabaseOrTempObjectIdEquatable,
	IDatabaseOrTempObjectIdSerializable
{
	public readonly tempId: Guid;
	public readonly hasDatabaseId: false;

	constructor(tempId: Guid) {
		this.tempId = tempId;
		this.hasDatabaseId = false;
	}

	public equals(other: DatabaseOrTempObjectId) {
		if (other.hasDatabaseId === true) {
			return false;
		}

		return other.tempId === this.tempId;
	}

	public toString(): string {
		return `temp-${this.tempId}`;
	}
}

type DatabaseOrTempObjectId = (IDatabaseId | ITempId)
	& IDatabaseOrTempObjectIdEquatable
	& IDatabaseOrTempObjectIdSerializable;

function convertDatabaseOrTempObjectIdToDto(id: DatabaseOrTempObjectId): DatabaseOrTempDtoId {
	if (id.hasDatabaseId === true) {
		return {
			hasDatabaseId: true,
			databaseId: id.databaseId
		};
	}

	return {
		hasDatabaseId: false,
		tempId: id.tempId
	};
}

function convertFromDtoToDatabaseOrTempObjectId(dto: DatabaseOrTempDtoId): DatabaseOrTempObjectId {
	if (dto.hasDatabaseId === true) {
		return new DatabaseId(dto.databaseId);
	}

	return new TempId(dto.tempId);
}

function normalizeDatabaseOrTempObjectIdKey(key: DatabaseOrTempObjectId): string {
	if (key.hasDatabaseId === true) {
		return `db_${key.databaseId}`;
	}

	return `temp_${key.tempId}`;
};

class DatabaseOrTempObjectIdMap<TValue> extends NormalizedMap<DatabaseOrTempObjectId, TValue> {
	public constructor() {
		super(normalizeDatabaseOrTempObjectIdKey);
	}
}

export {
	type DatabaseOrTempObjectId,
	TempId,
	DatabaseId,
	DatabaseOrTempObjectIdMap,
	convertDatabaseOrTempObjectIdToDto,
	convertFromDtoToDatabaseOrTempObjectId
};